import { Button, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import {
  EntityDescribedProperty,
  EntityDescribedPropertyFormat,
} from '../../../../types';
import { IP_LOOKUP_BASE_URL } from '../../../constants';
import { JupiterTheme } from '../../../theme/types';
import SafeURL from '../../SafeURL';
import { formatDateTime, sanitizeDate } from '../../util/formatter';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    propertyValue: {
      gridColumn: 'span 2',
      lineHeight: 1.35,
      marginBottom: theme.spacing(2),
      marginRight: theme.spacing(3),
      wordBreak: 'break-all',
      '& pre': {
        background: theme.palette.coolGrey[50],
        borderRadius: 3,
        fontFamily: `'Ubuntu Mono', monospace`,
        fontSize: theme.typography.caption.fontSize,
        margin: 0,
        width: 351,
        overflowX: 'auto',
        padding: theme.spacing(0.5),
      },
    },
    webLinkButton: {
      color: theme.palette.primary.main,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      letterSpacing: 'normal',
      marginLeft: -theme.spacing(1),
      marginRight: -theme.spacing(1),
      textTransform: 'none',
    },
    webLinkText: {
      '& a, & div': {
        color: theme.palette.primary.main,
        fontSize: theme.typography.body1.fontSize,
        lineHeight: 1.3,
        letterSpacing: 'normal',
        textTransform: 'none',
        textAlign: 'left',
      },
    },
  })
);

export const reducePropertyType = (property: EntityDescribedProperty) => {
  switch (property.type) {
    case EntityDescribedPropertyFormat.BOOLEAN:
      return <BooleanProperty property={property} />;
    case EntityDescribedPropertyFormat.JSON:
      return <JSONProperty property={property} />;
    case EntityDescribedPropertyFormat.MARKDOWN:
      return <MarkdownProperty property={property} />;
    case EntityDescribedPropertyFormat.URI:
      return <URIProperty property={property} />;
    case EntityDescribedPropertyFormat.EMAIL:
      return <EmailProperty property={property} />;
    case EntityDescribedPropertyFormat.IP:
    case EntityDescribedPropertyFormat.IPV4:
    case EntityDescribedPropertyFormat.IPV6:
      return <IPProperty property={property} />;
    case EntityDescribedPropertyFormat.DATE:
    case EntityDescribedPropertyFormat.DATETIME:
    case EntityDescribedPropertyFormat.UTCDATE:
    case EntityDescribedPropertyFormat.ISODATE:
    case EntityDescribedPropertyFormat.LOCALTIME:
      return <DateProperty property={property} />;
    case EntityDescribedPropertyFormat.MONEY:
      return <MoneyProperty property={property} />;
    default:
      return <UnknownProperty property={property} />;
  }
};

interface PropertyProps {
  property: EntityDescribedProperty;
}

const BooleanProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  return (
    <Typography variant="body2" className={classes.propertyValue}>
      {property.value.toString()}
    </Typography>
  );
};

const JSONProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  const value =
    typeof property.value === 'string'
      ? JSON.stringify(JSON.parse(property.value), null, 2)
      : JSON.stringify(property.value, null, 2);
  return (
    <div className={classes.propertyValue}>
      <pre>{value}</pre>
    </div>
  );
};

const MarkdownProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  return (
    <div className={classes.propertyValue}>
      <pre>{property.value}</pre>
    </div>
  );
};

const URIProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  const linkLength = (property.value as string).length;
  const linkText =
    linkLength > 42
      ? (property.value as string).substring(0, 18) +
        '…' +
        (property.value as string).substring(linkLength - 12)
      : property.value;
  return (
    <div className={classes.propertyValue}>
      <Button
        variant="text"
        color="primary"
        classes={{
          root: classes.webLinkButton,
          label: classes.webLinkText,
        }}
      >
        <SafeURL url={property.value} displayText={linkText} />
      </Button>
    </div>
  );
};

const EmailProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  const mailto = `mailto:${property.value}`;
  return (
    <div className={classes.propertyValue}>
      <Button
        variant="text"
        color="primary"
        classes={{
          root: classes.webLinkButton,
          label: classes.webLinkText,
        }}
      >
        <SafeURL url={mailto} displayText={property.value} />
      </Button>
    </div>
  );
};

const IPProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  if (typeof property.value !== 'string') {
    return (
      <div className={classes.propertyValue}>
        <Button
          variant="text"
          disabled
          classes={{
            root: classes.webLinkButton,
            label: classes.webLinkText,
          }}
        >
          {property.value}
        </Button>
      </div>
    );
  }
  const link = `${IP_LOOKUP_BASE_URL}${property.value.split('/')[0]}`;
  return (
    <div className={classes.propertyValue}>
      <Button
        variant="text"
        color="primary"
        classes={{
          root: classes.webLinkButton,
          label: classes.webLinkText,
        }}
      >
        <SafeURL url={link} displayText={property.value} />
      </Button>
    </div>
  );
};

const DateProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  const parsedDate = sanitizeDate(property.value);
  if (!parsedDate)
    return (
      <Typography variant="body2" className={classes.propertyValue}>
        Invalid Date
      </Typography>
    );
  if (property.value < 1) {
    return (
      <Typography variant="body2" className={classes.propertyValue}>
        {property.value}
      </Typography>
    );
  }
  let content;
  switch (property.type) {
    case EntityDescribedPropertyFormat.DATE:
      content = parsedDate.toISOString();
      break;
    case EntityDescribedPropertyFormat.DATETIME:
      content = parsedDate.toISOString();
      break;
    case EntityDescribedPropertyFormat.UTCDATE:
      content = parsedDate.toUTCString();
      break;
    case EntityDescribedPropertyFormat.ISODATE:
      content = parsedDate.toISOString();
      break;
    case EntityDescribedPropertyFormat.LOCALTIME:
      content = formatDateTime(parsedDate, true);
      break;
    default:
      break;
  }
  return (
    <Typography variant="body2" className={classes.propertyValue}>
      {content}
    </Typography>
  );
};

const MoneyProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  return (
    <Typography variant="body2" className={classes.propertyValue}>
      {property.value.toFixed(2).toString()}
    </Typography>
  );
};

const UnknownProperty = ({ property }: PropertyProps) => {
  const classes = useStyles();
  return (
    <Typography variant="body2" className={classes.propertyValue}>
      {String(property.value)}
    </Typography>
  );
};
